import type { ReviewFormDto } from '@flexbyt/shared';
import { backdropFor, readableTextOn } from '../../lib/contrast.js';
import { cx } from '@flexbyt/widget-preview';
import { useEffect, useId, useRef, useState } from 'react';
import { celebrateFiveStars } from '../../lib/celebrate.js';
import { formOptions, submitReview } from '../../lib/review-form.js';
import { HeadshotPicker } from './HeadshotPicker.js';
import { MAX_PHOTOS, PhotoPicker } from './PhotoPicker.js';
import { RatingMark, Stars, isLit, type RatingStyle } from './Stars.js';
import {
    Body,
    ExternalSites,
    FormField,
    PrimaryButton,
    Screen,
    Title,
} from './screens.js';

/**
 * The hosted review form.
 *
 * ---------------------------------------------------------------------------
 * Four screens, one form element
 * ---------------------------------------------------------------------------
 * Welcome, the review, who they are, thank you — the legacy order, because it
 * is the order the copy in every account is written for. They are steps of one
 * `<form>` rather than four forms, so a browser's own autofill, a password
 * manager and the Enter key all behave the way they do everywhere else.
 *
 * Only the last step submits. The two before it advance, which is why they are
 * `type="button"` — a bare `<button>` inside a form is a submit button, and
 * that single default is how a multi-step form ends up posting a half-filled
 * review the moment somebody presses Enter on the first field.
 *
 * ---------------------------------------------------------------------------
 * Validation is per-step
 * ---------------------------------------------------------------------------
 * Each step checks its own fields before it will advance, so nobody reaches the
 * last screen, presses Submit and is told the problem was two screens ago. The
 * server validates again — it has to, this endpoint is public — and its
 * messages are surfaced rather than replaced.
 */

const STEPS = ['welcome', 'review', 'about', 'thanks'] as const;
type Step = (typeof STEPS)[number];

/**
 * Starting points for the title, picked from the rating.
 *
 * Most people know how it went before they know what to call it, and a blank
 * "Enter Review Title" field is where a thirty-second review stalls into a
 * chore. Filling it in the moment a star is tapped gives them something true
 * to the score to edit or replace, rather than a blank field to stare at.
 *
 * Several per rating, chosen at random, rather than one. A single fixed
 * string per score is what a business ends up with a wall of five-star
 * reviews all titled "Amazing!" — every one of them looking like the same
 * unedited default, which undermines the very thing a title is meant to add.
 */
const TITLE_SUGGESTIONS: Record<number, string[]> = {
  1: ['Really disappointed', 'Would not recommend', 'Not good at all', 'Below expectations'],
  2: ['Not what I expected', 'Room for improvement', 'Fell a bit short', 'Mixed feelings'],
  3: ['It was okay', 'Decent, nothing special', 'Pretty average', 'Fine, could be better'],
  4: ['Really happy with this', 'Great experience', 'Would come back', 'Solidly good'],
  5: ['Amazing!', 'Couldn’t be happier', 'Exceeded expectations', 'Absolutely fantastic'],
};

function suggestedTitle(rating: number): string {
  const options = TITLE_SUGGESTIONS[rating];
  if (!options) return '';
  return options[Math.floor(Math.random() * options.length)]!;
}

export function isStep(value: unknown): value is Step {
  return STEPS.includes(value as Step);
}

export function ReviewForm({
  form,
  slug,
  initialStep = 'welcome',
  contact,
  requestId,
  productId,
  /** Injected by the tests; the real page posts to the API. */
  submit = submitReview,
  celebrate = celebrateFiveStars,
}: {
  form: ReviewFormDto;
  slug: string;
  initialStep?: Step;
  /** Filled in from `?email=<requestId>` when the link came from an invite. */
  contact?: { contactName: string; contactEmail: string } | null;
  /**
   * The same `?email=<requestId>` that filled in `contact`, carried through
   * to the submission itself rather than just used to prefill two fields.
   *
   * It is what lets the review this produces be marked verified — somebody
   * was actually asked, by name, rather than having found this page on their
   * own — and be linked back to the request that sent them here. Undefined
   * for every other way into this form, which get neither.
   */
  requestId?: string;
  /**
   * From `?product=<id>`, set only by the product widget's own link.
   *
   * It makes this a review *of that product* rather than of the business. The
   * form looks and behaves identically either way — the reviewer is on a
   * product page and already knows what they are reviewing, so saying so again
   * on the form would be noise.
   */
  productId?: string;
  submit?: typeof submitReview;
  /**
   * What a five-star submission sets off — the fireworks, unless the site
   * opted out of them (pass `null`). Injectable so tests can see it called.
   */
  celebrate?: (() => void) | null;
}) {
  const [step, setStep] = useState<Step>(initialStep);

  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  /*
    Whether the title is still the rating's own suggestion or something the
    reviewer typed themselves. Picking a different star afterwards should
    replace a suggestion nobody has read yet; it should never overwrite three
    words somebody just wrote.
  */
  const [titleTouched, setTitleTouched] = useState(false);
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);

  /*
    Prefilled from the invitation when there is one. Held in state rather than
    read straight from the prop so somebody can still correct it — a review
    request sent to "Sam" may well be filled in by their colleague, and a name
    the reviewer cannot change is a name that ends up wrong on a public page.
  */
  const [name, setName] = useState(contact?.contactName ?? '');
  const [email, setEmail] = useState(contact?.contactEmail ?? '');
  const [role, setRole] = useState('');
  const [avatar, setAvatar] = useState<File | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const ids = useId();
  const field = (key: string) => `${ids}-${key}`;

  const { primaryColor } = form.appearance;
  /** Whichever of white or near-black reads on the brand colour. */
  const onPrimary = readableTextOn(primaryColor);
  // Newer than the published `@flexbyt/shared`, so read with care.
  const ratingStyle: RatingStyle =
    (form.appearance as { ratingStyle?: RatingStyle }).ratingStyle ?? 'stars';
  const t = form.translation;
  const options = formOptions(form);

  /*
    The page colour — a tint of the form's own colour — on the page rather
    than on a card.

    Set on `<body>` because this route fills the viewport: a coloured card on a
    white page is a different design from the one the editor previews, and on a
    phone the difference is the whole screen. Restored on unmount so a client
    navigation away from the form does not leave the marketing site tinted.
  */
  useEffect(() => {
    const chosen = backdropFor(form.appearance.primaryColor);
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = chosen;
    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, [form.appearance.primaryColor]);

  function goTo(next: Step) {
    setErrors({});
    setStep(next);
  }

  /*
    Focus follows the step, onto the new screen's heading.

    Without it the focus stays on a button that is now `hidden`, which browsers
    handle by dropping it to the document — so a keyboard visitor's next Tab
    restarts at the top of the page, and a screen reader announces nothing at
    all about having moved.

    An effect rather than a `requestAnimationFrame` inside `goTo`. React has
    already committed the DOM by the time this runs, so the heading exists; and
    unlike a stray animation frame, it cannot fire after the form has gone. That
    is not hypothetical — the frame version reached into whatever document
    existed when it eventually ran and moved the focus there.

    `first` skips the initial render. Stealing focus on page load would scroll
    a phone past the logo before the reader has read anything, and there is no
    transition to announce yet.
  */
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    document.getElementById(`${ids}-${step}-heading`)?.focus();
  }, [step, ids]);

  function chooseRating(next: number) {
    setRating(next);
    setErrors(({ rating: _removed, ...rest }) => rest);
    if (!titleTouched) setTitle(suggestedTitle(next));
  }

  function leaveReviewStep() {
    const found: Record<string, string> = {};
    if (rating < 1) found.rating = 'Please choose a rating.';
    if (!content.trim()) found.content = 'Please write a few words.';
    if (Object.keys(found).length > 0) return setErrors(found);
    goTo('about');
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (sending) return;

    const found: Record<string, string> = {};
    if (!name.trim()) found.name = 'Your name, please.';
    if (!email.trim()) {
      found.email = 'Your email, please.';
    } else if (!email.includes('@')) {
      found.email = 'That does not look like an email address.';
    }
    if (options.askRole === 'required' && !role.trim()) {
      found.role = 'Your role or company, please.';
    }
    if (Object.keys(found).length > 0) return setErrors(found);

    setSending(true);
    setFailure(null);

    try {
      await submit(slug, {
        rating,
        authorRole: options.askRole !== 'off' ? role.trim() : undefined,
        title: title.trim(),
        content: content.trim(),
        authorName: name.trim(),
        authorEmail: email.trim(),
        photos,
        avatar: options.askPhoto ? avatar : null,
        productId,
        requestId,
      });
      goTo('thanks');
      // The best answer a customer can give gets a celebration, as the old
      // form did it: fireworks over the thank-you screen.
      if (rating === 5) celebrate?.();
    } catch (error) {
      /*
        The review stays on screen. Somebody has just written about their
        experience, and clearing the form on a network failure — or advancing to
        a thank-you that is not true — loses work they cannot get back.
      */
      setFailure(error instanceof Error ? error.message : 'Your review could not be sent.');
    } finally {
      setSending(false);
    }
  }

  const external = form.welcomeScreen.externalSites ?? [];
  const sendingElsewhere =
    form.welcomeScreen.collectWithExternalSites && external.length > 0;

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="max-sm:flex max-sm:flex-1 max-sm:flex-col"
      // The brand colour, for the fields' focus rings and the photo tiles.
      style={{ '--rj-brand': primaryColor } as React.CSSProperties}
    >
      <Screen visible={step === 'welcome'}>
        {/*
          The first screen is the business's own colour, edge to edge of the
          card: a bold, confident ask rather than grey text on white. Text and
          stars take whichever of white or near-black reads on that colour,
          and two soft shapes in the same tone keep the flat colour from
          looking like a block.
        */}
        <Hero full color={primaryColor} on={onPrimary} logoUrl={form.logoUrl}>
            <Title id={`${ids}-welcome-heading`} onColour={onPrimary}>
              {form.welcomeScreen.title}
            </Title>
            <Body onColour={onPrimary}>{form.welcomeScreen.message}</Body>

            {sendingElsewhere ? (
              <ExternalSites
                sites={external}
                prompt={form.welcomeScreen.externalLinkPrompt}
              />
            ) : (
              <>
                {/*
                  The first question is the rating, asked big. One tap answers
                  it and moves on — there is no button, choosing is the
                  action. A keyboard moves with the arrows and chooses with
                  Enter or Space, so arrowing through never skips ahead.
                */}
                <Stars
                  size="lg"
                  ratingStyle={ratingStyle}
                  value={rating}
                  onChange={chooseRating}
                  onPick={(next) => {
                    chooseRating(next);
                    // A beat to see the stars fill before the screen changes.
                    window.setTimeout(() => goTo('review'), 180);
                  }}
                  color={onPrimary}
                  label={form.welcomeScreen.title}
                />
              </>
            )}
        </Hero>
      </Screen>

      <Screen visible={step === 'review'}>
        <Hero
          color={primaryColor}
          on={onPrimary}
          logoUrl={form.logoUrl}
          onBack={sending ? undefined : () => goTo('welcome')}
        >
          <Title id={`${ids}-review-heading`} onColour={onPrimary}>
            {form.reviewScreen.title}
          </Title>

          {/*
            The rating already given on the first screen, as a reference — not
            a second question. Changing it is the back arrow's job. Only a link
            that opens straight onto this screen arrives without one, and then
            the scale is the way to give it.
          */}
          {rating > 0 ? (
            <RatingReference rating={rating} color={onPrimary} ratingStyle={ratingStyle} />
          ) : (
            <Stars
              value={rating}
              onChange={chooseRating}
              color={onPrimary}
              label={form.reviewScreen.title}
              ratingStyle={ratingStyle}
            />
          )}
        </Hero>
        {errors.rating && (
          <p role="alert" className="mt-1.5 text-micro text-critical-700">
            {errors.rating}
          </p>
        )}

        <Body>{form.reviewScreen.prompt}</Body>

        <FormField
          id={field('title')}
          label={t.enterReviewTitle}
          value={title}
          onChange={(event) => {
            setTitleTouched(true);
            setTitle(event.target.value);
          }}
          maxLength={200}
        />
        <FormField
          id={field('content')}
          as="textarea"
          rows={5}
          label={t.writeYourReview}
          value={content}
          error={errors.content}
          onChange={(event) => setContent(event.target.value)}
          maxLength={5000}
        />

        {form.reviewScreen.allowPhotos && (
          <PhotoPicker
            photos={photos}
            onChange={setPhotos}
            label={form.reviewScreen.addPhotosLabel}
            hint={form.reviewScreen.photoUploadPrompt}
          />
        )}

        <PrimaryButton type="button" color={primaryColor} onClick={leaveReviewStep}>
          {t.continueLabel}
        </PrimaryButton>
      </Screen>

      <Screen visible={step === 'about'}>
        <Hero
          color={primaryColor}
          on={onPrimary}
          logoUrl={form.logoUrl}
          onBack={sending ? undefined : () => goTo('review')}
        >
          <Title id={`${ids}-about-heading`} onColour={onPrimary}>
            {form.userInfo.title}
          </Title>
          <Body onColour={onPrimary}>{form.userInfo.description}</Body>
        </Hero>

        <FormField
          id={field('name')}
          label={t.yourNameLabel}
          placeholder={t.yourNamePlaceholder}
          value={name}
          error={errors.name}
          autoComplete="name"
          onChange={(event) => setName(event.target.value)}
          maxLength={120}
        />
        <FormField
          id={field('email')}
          type="email"
          label={t.yourEmailLabel}
          placeholder={t.yourEmailPlaceholder}
          value={email}
          error={errors.email}
          hint={t.emailAddressPrivacyNote}
          autoComplete="email"
          onChange={(event) => setEmail(event.target.value)}
        />
        {options.askRole !== 'off' && (
          <FormField
            id={field('role')}
            label={options.roleLabel}
            placeholder={options.rolePlaceholder}
            value={role}
            error={errors.role}
            autoComplete="organization-title"
            required={options.askRole === 'required'}
            onChange={(event) => setRole(event.target.value)}
            maxLength={120}
          />
        )}
        {options.askPhoto && (
          <HeadshotPicker
            value={avatar}
            onChange={setAvatar}
            label={options.photoLabel}
          />
        )}

        {failure && (
          <p role="alert" className="mt-4 text-caption text-critical-700">
            {failure}
          </p>
        )}

        <PrimaryButton type="submit" color={primaryColor} disabled={sending}>
          {sending ? 'Sending…' : t.submitButtonLabel}
        </PrimaryButton>
      </Screen>

      <Screen visible={step === 'thanks'}>
        {/* The finish, in the business's colour edge to edge, with a tick. */}
        <Hero full color={primaryColor} on={onPrimary} logoUrl={form.logoUrl}>
          <span
            aria-hidden
            className="mb-5 grid size-14 place-items-center rounded-full text-[1.6rem] font-bold"
            style={{ backgroundColor: onPrimary, color: primaryColor }}
          >
            ✓
          </span>
          <Title id={`${ids}-thanks-heading`} onColour={onPrimary}>
            {form.successScreen.pageTitle}
          </Title>
          <Body onColour={onPrimary}>{form.successScreen.message}</Body>

          {form.successScreen.callToActionLabel && form.successScreen.callToActionUrl && (
            <a
              href={form.successScreen.callToActionUrl}
              className={cx(
                'mt-6 block w-full rounded-lg bg-white px-4 py-3 text-center text-caption font-semibold text-ink-950',
                'transition-[filter] duration-[var(--duration-fast)] hover:brightness-95',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600'
              )}
            >
              {form.successScreen.callToActionLabel}
            </a>
          )}
        </Hero>
      </Screen>
    </form>
  );
}

export { MAX_PHOTOS };

/** The chosen rating, drawn small and fixed: what this review is about to say. */
function RatingReference({
  rating,
  color,
  ratingStyle,
}: {
  rating: number;
  color: string;
  ratingStyle: RatingStyle;
}) {
  return (
    <p className="mt-3 flex items-center gap-1" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((mark) => (
        <RatingMark
          key={mark}
          style={ratingStyle}
          mark={mark}
          lit={isLit(ratingStyle, mark, rating)}
          color={color}
          className="size-5 text-[1.05rem]"
        />
      ))}
    </p>
  );
}

/**
 * The coloured part of every screen.
 *
 * The business's colour with two soft shapes in the same tone, the logo on a
 * white chip so any logo reads on any colour, and the way back when there is
 * one. Edge to edge on the first and last screens; a header over the white
 * fields on the two between.
 */
function Hero({
  color,
  on,
  full = false,
  logoUrl,
  onBack,
  children,
}: {
  color: string;
  on: string;
  full?: boolean;
  logoUrl: string;
  onBack?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cx(
        'relative overflow-hidden px-6',
        full
          ? // On a phone, the whole screen: content centred in the colour.
            '-m-6 rounded-[10px] pt-7 pb-16 max-sm:flex max-sm:flex-1 max-sm:flex-col max-sm:justify-center max-sm:rounded-none max-sm:pt-12'
          : '-mx-6 -mt-6 mb-6 rounded-t-[10px] pt-6 pb-12 max-sm:rounded-none'
      )}
      style={{ backgroundColor: color, color: on }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 size-48 rounded-full"
        style={{ backgroundColor: on, opacity: 0.08 }}
      />
      <svg
        aria-hidden
        viewBox="0 0 400 60"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-12 w-full"
      >
        <path
          d="M0 30 C 70 5, 130 55, 200 30 S 330 5, 400 28 L 400 60 L 0 60 Z"
          fill={full ? on : '#ffffff'}
          opacity={full ? 0.1 : 1}
        />
      </svg>

      <div className="relative">
        {(logoUrl || onBack) && (
          <div className="mb-5 flex items-center justify-between gap-3">
            {logoUrl ? (
              <span className="inline-flex rounded-xl bg-white px-3 py-2 shadow-sm">
                <img src={logoUrl} alt="" className="max-h-9 max-w-[160px] object-contain" />
              </span>
            ) : (
              <span />
            )}
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="grid size-9 shrink-0 place-items-center rounded-full transition-[background-color] hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ backgroundColor: 'rgb(255 255 255 / 0.12)' }}
              >
                <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden>
                  <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="sr-only">Back</span>
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
